import {
	getAnimalQueryOptions,
	listHerdsByOrganizationQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type { CreateOperationMutationRequest } from "@packages/api-client/types/livestockController";
import type { LivestockHerdWithStats } from "@packages/shared-types/livestock";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCreateOperation } from "@/api/livestock";
import { PageHeader } from "@/components/PageHeader";
import {
	findOfflineAnimalRow,
	useAnimalOptions,
} from "@/hooks/useAnimalOptions";
import { OperationForm } from "../../../operations/-components/OperationForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/animals/$animalId/operations/new",
)({
	staticData: { offline: "ready" },
	loader: ({ context, params }) => {
		// Fire-and-forget prefetch (animals/new precedent): if cached (online or
		// from IDB), the component sees data immediately; if not cached offline,
		// the form still renders — animal options fall back to the prepared
		// dataset via useAnimalOptions, herd options degrade to empty.
		context.queryClient.ensureQueryData({
			...getAnimalQueryOptions(params.orgId, params.animalId),
			staleTime: Number.POSITIVE_INFINITY,
		});
		context.queryClient.ensureQueryData({
			...listHerdsByOrganizationQueryOptions(params.orgId, {
				establishmentId: params.establishmentId,
			}),
			staleTime: Number.POSITIVE_INFINITY,
		});
	},
	component: NewAnimalOperationPage,
});

function NewAnimalOperationPage() {
	const { orgId, establishmentId, animalId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();
	const queryClient = useQueryClient();

	const { data: animal } = useQuery(getAnimalQueryOptions(orgId, animalId));

	const { data: herdsData } = useQuery(
		listHerdsByOrganizationQueryOptions(orgId, { establishmentId }),
	);

	const { options } = useAnimalOptions({ orgId, establishmentId });

	const createOperation = useCreateOperation();

	const herds = (herdsData?.data ?? []) as LivestockHerdWithStats[];

	const selectedAnimal = options.find((a) => a.id === animalId);
	const animalOptions = selectedAnimal ? [selectedAnimal] : [];

	// Herd lock comes from the animal detail when cached; offline without it,
	// the prepared dataset row carries the same herd summary.
	const herdId =
		animal?.herd?.id ??
		findOfflineAnimalRow(queryClient, orgId, establishmentId, animalId)?.herd
			?.id;
	const herdOptions = herdId ? herds.filter((h) => h.id === herdId) : herds;

	const goBackToAnimal = () => {
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId",
			params: { orgId, establishmentId, animalId },
		});
	};

	const handleSubmit = (
		data: CreateOperationMutationRequest,
		options?: { addAnother: boolean },
	) =>
		createOperation.mutateAsync(
			{ organizationId: orgId, data },
			{ onSuccess: options?.addAnother ? undefined : goBackToAnimal },
		);

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={t("livestock.operations.create.title")}
				showBackButton
			/>

			<OperationForm
				orgId={orgId}
				establishmentId={establishmentId}
				herds={herdOptions}
				animals={animalOptions}
				lockedAnimalId={animalId}
				lockedHerdId={herdId}
				onSubmit={handleSubmit}
				isSubmitting={createOperation.isPending}
				onCancel={goBackToAnimal}
			/>
		</div>
	);
}
